import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Theme store shape:
 *   preference: 'light' | 'dark' | 'system'  — what the user picked, persisted
 *   systemMode: 'light' | 'dark'             — what the OS currently says, live
 *
 * Only `preference` is persisted. The resolved mode is derived, so a viewer on
 * 'system' follows their OS without us writing anything back to storage.
 */
const QUERY = '(prefers-color-scheme: dark)';

const readSystemMode = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(QUERY).matches ? 'dark' : 'light';
};

/** The mode actually in force, given a store state. */
export const resolveMode = (state) =>
  state.preference === 'system' ? state.systemMode : state.preference;

const useThemeStore = create(
  persist(
    (set, get) => ({
      preference: 'system',
      systemMode: readSystemMode(),

      setPreference: (preference) => set({ preference }),

      /** Flip to the opposite of what is on screen, leaving 'system' behind. */
      toggle: () => set({ preference: resolveMode(get()) === 'dark' ? 'light' : 'dark' }),
    }),
    {
      name: 'evolve-theme',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ preference: state.preference }),
      // A stored preference could be anything; never hand `makeTheme` a value
      // it does not know.
      merge: (persisted, current) => ({
        ...current,
        preference: ['light', 'dark', 'system'].includes(persisted?.preference)
          ? persisted.preference
          : 'system',
      }),
    }
  )
);

// Follow the OS while the preference is 'system'. One listener for the app.
if (typeof window !== 'undefined' && window.matchMedia) {
  const mq = window.matchMedia(QUERY);
  const onChange = (e) =>
    useThemeStore.setState({ systemMode: e.matches ? 'dark' : 'light' });
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else if (mq.addListener) mq.addListener(onChange);
}

/** Convenience hook: the resolved 'light' | 'dark'. */
export const useMode = () => useThemeStore(resolveMode);

export default useThemeStore;
